import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Wrench, ShieldCheck, Shield, Award,
  Calculator, Calendar, Sparkles, Star,
  CheckCircle, XCircle, ArrowRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import Hero from '../components/Hero';
import { services, trustReasons, howItWorks, reviews, comparisonData } from '../utils/mockData';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { faqs } from '../utils/mockData';

import GoogleReviewButton from '../components/GoogleReviewButton';

import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination } from 'swiper/modules';

import 'swiper/css';
import 'swiper/css/pagination';

import GallerySection from '../components/GallerySection';


const iconMap = {
  Tool: Wrench,
  ShieldCheck: ShieldCheck,
  Shield: Shield,
  Award: Award,
  Calculator: Calculator,
  Calendar: Calendar,
  Sparkles: Sparkles,
  Star: Star
};

const Home = () => {
  const [homeReviews, setHomeReviews] = useState(reviews);

  useEffect(() => {
    const backendBaseUrl = (() => {
      const fromEnv = (process.env.REACT_APP_BACKEND_URL || '').trim();
      const useLocalBackend = String(process.env.REACT_APP_USE_LOCAL_BACKEND || '').trim().toLowerCase() === 'true';
      const isBrowser = typeof window !== 'undefined' && window.location;
      const hostname = isBrowser ? window.location.hostname : '';
      const isLocalFrontend = hostname === 'localhost' || hostname === '127.0.0.1';

      const normalize = (value) => value.replace(/\/+$/, '');

      if (fromEnv) {
        const normalized = normalize(fromEnv);
        const isLocalBackend = /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/i.test(normalized);
        if (isLocalBackend && (!isLocalFrontend || !useLocalBackend)) {
          return 'https://server.royalshine.com.au';
        }
        return normalized;
      }

      return 'https://server.royalshine.com.au';
    })();
    const controller = new AbortController();

    (async () => {
      try {
        const response = await fetch(`${backendBaseUrl}/api/public/google-reviews?limit=6`, {
          signal: controller.signal,
        });
        if (!response.ok) return;
        const payload = await response.json();
        const list = payload?.data?.reviews;
        if (Array.isArray(list) && list.length > 0) {
          setHomeReviews(list);
        }
      } catch {
        // Ignore and fall back to mock reviews
      }
    })();

    return () => controller.abort();
  }, []);

  return (
    <div className="min-h-screen">
      <GoogleReviewButton />
      {/* Hero Section */}
      <Hero />

      {/* Welcome Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-4xl font-bold text-slate-900 mb-6">
              Welcome to Royal Shine, the most trusted and top-rated cleaning company in Perth.
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed">
              Our expert team delivers top-rated cleaning services leaving your home spotless and inviting. With our commitment to quality, reliability, and customer satisfaction, we guarantee a hassle-free cleaning experience that will exceed your expectations.
            </p>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Our Services</h2>
            <p className="text-lg text-slate-600">
              We offer both Once-Off Cleaning and Recurring Cleaning so you can choose the service that fits your needs.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.slice(0, 4).map((service) => (
              <Card
                key={service.id}
                className="overflow-hidden hover:shadow-xl transition-all duration-300 group relative"
              >
                {/* Badges */}
                {service.id === 'commercial' && (
                  <div className="absolute top-4 left-4 bg-slate-900/80 text-white px-3 py-1 rounded-full text-xs font-bold z-10">
                    CONTACT US
                  </div>
                )}

                {service.popular && (
                  <div className="absolute top-4 right-4 bg-blue-600 text-white px-3 py-1 rounded-full text-xs font-bold z-10">
                    MOST POPULAR!
                  </div>
                )}

                {/* Image Slider */}
                <div className="aspect-video overflow-hidden">
                  <Swiper
                    modules={[Autoplay, Pagination]}
                    autoplay={{ delay: 3000, disableOnInteraction: false }}
                    pagination={{ clickable: true }}
                    loop
                    className="h-full"
                  >
                    {service.image.map((img, index) => (
                      <SwiperSlide key={index}>
                        <img
                          src={img}
                          alt={`${service.name} ${index + 1}`}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                        />
                      </SwiperSlide>
                    ))}
                  </Swiper>
                </div>

                {/* Content */}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-slate-900 mb-2">
                    {service.name}
                  </h3>

                  <ul className="space-y-2 mb-6">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="text-sm text-slate-600 flex items-start">
                        <span className="text-blue-600 mr-2">•</span>
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <div className="flex space-x-2">
                    <Button asChild size="sm" className="flex-1 bg-blue-600 hover:bg-blue-700">
                      <Link to={service.id === 'commercial' ? '/contact' : '/book'}>
                        Book Now
                      </Link>
                    </Button>

                    <Button asChild size="sm" variant="outline" className="flex-1">
                      <Link
                        to={service.id === 'commercial'
                          ? '/contact'
                          : `/services/${service.id}`}
                      >
                        {service.id === 'commercial' ? 'Contact Us' : 'Learn More'}
                      </Link>
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button asChild size="lg" variant="outline">
              <Link to="/services">View All Services</Link>
            </Button>
          </div>
        </div>
      </section>


      {/* Gallery Section */}
      <GallerySection />



      {/* Why Trust Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Why Trust Our Cleaning Professionals</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {trustReasons.map((reason, idx) => {
              const IconComponent = iconMap[reason.icon];
              return (
                <div key={idx} className="text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                    <IconComponent className="w-8 h-8 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">{reason.title}</h3>
                  <p className="text-slate-600">{reason.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">How Does It Work?</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {howItWorks.map((step) => {
              const IconComponent = iconMap[step.icon];
              return (
                <div key={step.step} className="relative">
                  <div className="bg-white rounded-lg p-6 shadow-md h-full hover:shadow-xl transition-shadow">
                    <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-600 text-white rounded-full font-bold text-xl mb-4">
                      {step.step}
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-3">{step.title}</h3>
                    <p className="text-slate-600">{step.description}</p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Button asChild size="lg" className="bg-blue-600 hover:bg-blue-700">
              <Link to="/book">
                Book Now
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">
              <Link to="/reviews" className="hover:text-blue-600 transition-colors">
                100+ Reviews from Satisfied Clients
              </Link>
            </h2>
            <div className="flex justify-center items-center space-x-2 mb-4">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-6 h-6 fill-yellow-400 text-yellow-400" />
              ))}
              <span className="text-2xl font-bold text-slate-900 ml-2">/ 5</span>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {homeReviews.slice(0, 6).map((review) => (
              <Card key={review.id} className="p-6 hover:shadow-xl transition-shadow">
                <div className="flex items-center space-x-4 mb-4">
                  <img
                    src={review.avatar}
                    alt={review.name}
                    className="w-12 h-12 rounded-full"
                  />
                  <div>
                    <h4 className="font-semibold text-slate-900">{review.name}</h4>
                    <p className="text-sm text-slate-500">{review.date}</p>
                  </div>
                </div>
                <div className="flex space-x-1 mb-3">
                  {[...Array(review.rating)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-slate-600 text-sm">{review.text}</p>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button asChild size="lg" variant="outline">
              <Link to="/reviews">Read More Reviews</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Comparison Table Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">
              Compare our services to see what best fits your needs
            </h2>
          </div>

          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-900 text-white">
                  <tr>
                    <th className="px-6 py-4 text-left font-semibold">Cleaning Tasks</th>
                    <th className="px-6 py-4 text-center font-semibold">Standard Cleaning</th>
                    <th className="px-6 py-4 text-center font-semibold">Deep/Vacate Cleaning</th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonData.map((category, idx) => (
                    <React.Fragment key={idx}>
                      <tr className="bg-slate-100">
                        <td colSpan={3} className="px-6 py-3 font-bold text-slate-900">
                          {category.category}
                        </td>
                      </tr>
                      {category.tasks.map((task, taskIdx) => (
                        <tr key={taskIdx} className="border-t border-slate-200 hover:bg-slate-50">
                          <td className="px-6 py-3 text-slate-700">{task.name}</td>
                          <td className="px-6 py-3 text-center">
                            {task.standard ? (
                              <CheckCircle className="w-6 h-6 text-green-600 mx-auto" />
                            ) : (
                              <XCircle className="w-6 h-6 text-slate-300 mx-auto" />
                            )}
                          </td>
                          <td className="px-6 py-3 text-center">
                            {task.deep ? (
                              <CheckCircle className="w-6 h-6 text-green-600 mx-auto" />
                            ) : (
                              <XCircle className="w-6 h-6 text-slate-300 mx-auto" />
                            )}
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Frequently Asked Questions</h2>
          </div>

          <Accordion type="single" collapsible className="w-full">
            {faqs.slice(0, 4).map((faq, idx) => (
              <AccordionItem key={idx} value={`item-${idx}`}>
                <AccordionTrigger className="text-left font-semibold text-slate-900">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          <div className="text-center mt-8">
            <Button asChild variant="outline">
              <Link to="/faq">View All FAQs</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Trust Badges Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 items-center justify-items-center">
            <div className="text-center">
              <div className="text-5xl font-bold text-blue-600 mb-2">100%</div>
              <p className="text-slate-600 font-medium">Satisfaction</p>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-blue-600 mb-2">✓</div>
              <p className="text-slate-600 font-medium">Reliable Professionals</p>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-blue-600 mb-2">24hr</div>
              <p className="text-slate-600 font-medium">Free Cancellation</p>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-blue-600 mb-2">★</div>
              <p className="text-slate-600 font-medium">Top Quality Clean</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-blue-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-4">Customer Satisfaction</h2>
          <p className="text-xl mb-2">We take your satisfaction seriously.</p>
          <p className="text-lg mb-8 opacity-90">
            We are committed to 100% customer satisfaction. If you are not completely satisfied with our clean, we will send our team back to reclean or provide a partial refund.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button asChild size="lg" variant="secondary" className="bg-white text-blue-600 hover:bg-slate-100">
              <Link to="/book">Book Now</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
              <a href="tel:+61451233795">Call Us – +61 451 233 795</a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
